import { randomInt } from 'crypto';
import { PortableTextBlock, stegaClean, toPlainText } from 'next-sanity';
import { jwtDecode } from 'jwt-decode';
import { PageHub } from '@/constants';

export function randomIndex(count: number | undefined): number {
  const c = Math.max(1, Number(count) || 1);
  return randomInt(0, c);
}

function truncateText(text: string): string {
  const maxWords = 30;
  const maxChars = 175;

  const cleanText = stegaClean(text);
  const trimmedText = cleanText.trim();
  const words = trimmedText.split(/\s+/);
  let truncated = words.slice(0, maxWords).join(' ');
  if (truncated.length > maxChars) {
    truncated = truncated.slice(0, maxChars).trim();
  }
  if (truncated !== trimmedText) {
    truncated += '...';
  }
  return truncated;
}
export const summary = (content: PortableTextBlock[]) => {
  if (!content) {
    return '';
  }
  const plainText = toPlainText(content);
  const firstParagraph = plainText.split('\n\n')[0];
  return truncateText(firstParagraph);
  // return toPlainText(content).split('\n\n')[0];
};

/** Truncate a plain-text listing excerpt (from GROQ pt::text) for JSON-LD. */
export const listingDescription = (excerpt?: string | null) => {
  if (!excerpt) {
    return '';
  }
  const firstParagraph = excerpt.split('\n\n')[0];
  return truncateText(firstParagraph);
};

export const formatDate = (date: string) => {
  return new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(date as string));
};
export function convertDateFormat(dateString: string): string {
  // Create a Date object from the input string
  const date = new Date(dateString);

  // Define an array of month names
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  // Get the components of the date
  const year = date.getFullYear();
  const month = monthNames[date.getMonth()]; // Get month name
  const day = String(date.getDate()).padStart(2, '0'); // Pad day with leading zero

  // Format the date to "Month DD, YYYY"
  return `${month} ${day}, ${year}`;
}

export function schemaTypeAndPageMapping(
  schemaType: string,
  pageHubVal?: string | null
) {
  switch (schemaType) {
    case 'blog':
      return {
        type: 'blog',
        page: 'blog',
      };
    case 'resource':
      return {
        type: 'resource',
        page: 'resources',
      };
    case 'faq':
      return {
        type: 'faq',
        page: 'support',
      };
    case 'news':
      return {
        type: 'news',
        page: 'news',
      };
    case 'caseStudy':
      return {
        type: 'caseStudy',
        page: 'case-studies',
      };
    case 'careerType':
      return {
        type: 'careerType',
        page: 'careers',
      };
    case 'landingPage':
      return {
        type: 'landingPage',
        page: pageHubVal === PageHub.HOW_TO_USE ? PageHub.HOW_TO_USE : '',
      };
    case 'printable':
      return {
        type: 'printable',
        page: 'printables',
      };

    case 'about':
      return {
        type: 'about',
        page: 'about',
      };
    case 'advertise':
      return {
        type: 'advertise',
        page: 'advertisewithus',
      };
    case 'enterprise':
      return {
        type: 'enterprise',
        page: 'enterprise',
      };
    case 'findSignup':
      return {
        type: 'findSignup',
        page: 'findasignup',
      };
    case 'giveaway':
      return {
        type: 'giveaway',
        page: 'giveaway',
      };
    case 'help':
      return {
        type: 'help',
        page: 'help',
      };
    case 'homeBigBang':
      return {
        type: 'homeBigBang',
        page: 'home',
      };
    case 'brandkit':
      return {
        type: 'brandkit',
        page: 'media',
      };
    case 'pricing':
      return {
        type: 'pricing',
        page: 'pricing',
      };
    case 'pricingnew':
      return {
        type: 'pricingnew',
        page: 'pricing',
      };
    case 'enterpriseBigBang':
      return {
        type: 'enterpriseBigBang',
        page: 'enterprise',
      };
    case 'promotions':
      return {
        type: 'promotions',
        page: 'promotions',
      };
    case 'templates':
      return {
        type: 'templates',
        page: 'templates',
      };
    case 'socialImpact':
      return {
        type: 'socialImpact',
        page: 'social-impact',
      };
  }
}

interface DecodedToken {
  exp: number;
}

export const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<DecodedToken>(token);
    const currentTime = Math.floor(Date.now() / 1000);
    const bufferTime = 120; // 2 minutes buffer in seconds
    return decoded.exp <= currentTime + bufferTime;
  } catch {
    return true;
  }
};

// Helper function to extract domain from API URL
export const extractDomain = (apiUrl: string): string => {
  try {
    const url = new URL(apiUrl);
    return url.hostname.split('.').slice(-2).join('.'); // Gets "signupgenius.rocks"
  } catch {
    return 'signupgenius.com'; // Fallback
  }
};

export * from './types';

export const scrollToTop = () => {
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};
